import moment from "moment";

function toIdArray(value) {
  if (Array.isArray(value)) return value.map(Number).filter((n) => !Number.isNaN(n));
  if (value === null || value === undefined || value === "") return [];
  const n = Number(value);
  return Number.isNaN(n) ? [] : [n];
}

// "Expeditor / All" sentinel — null/undefined/0 means "show everything".
export function isExpeditor(printerId) {
  return (
    printerId === null ||
    printerId === undefined ||
    printerId === 0 ||
    printerId === "0"
  );
}

// Item override wins when it has real ids; otherwise inherit the category's.
export function resolvePrinterIds(item) {
  const own = toIdArray(item.printer_ids ?? item.printer_id);
  const usesOwn = own.length > 0 && !(own.length === 1 && own[0] === 0);
  if (usesOwn) return own;
  return toIdArray(item.category_printer_ids ?? item.category_printer_id);
}

export function itemMatchesPrinter(item, printerId) {
  if (isExpeditor(printerId)) return true;
  return resolvePrinterIds(item).includes(Number(printerId));
}

// Items shown on the selected printer. Modifiers/options ride with their
// parent — the filter is only evaluated at the parent-item level.
export function visibleItems(items, printerId) {
  if (!Array.isArray(items)) return [];
  if (isExpeditor(printerId)) return items;
  return items.filter((item) => itemMatchesPrinter(item, printerId));
}

// kitchen_status: 0 = preparing, 1 = ready (MintyTransaction::KITCHEN_STATUS_*).
// READY means every station the order routes to has pressed Done.
export const KITCHEN_STATUS_READY = 1;

export function isOrderKitchenReady(order) {
  return Number(order?.kitchen_status) === KITCHEN_STATUS_READY;
}

// Stations an order has items on — the same set the BE waits on before
// turning the order READY.
export function orderStationIds(order) {
  const ids = new Set();
  (order?.items ?? []).forEach((item) => {
    resolvePrinterIds(item).forEach((id) => {
      if (id > 0) ids.add(id);
    });
  });
  return [...ids];
}

// Has this station pressed Done on the order? A READY order counts as done
// everywhere, including orders moved as a whole before per-station Done.
export function isStationReady(order, stationId) {
  if (isOrderKitchenReady(order)) return true;
  return toIdArray(order?.ready_printer_ids).includes(Number(stationId));
}

// Local copy of what order/move-kds does with a printer_id, so the board can
// move the chit before the response lands. Returns the fields to patch.
export function toggleStationReady(order, stationId) {
  const stations = orderStationIds(order);
  let ready = isOrderKitchenReady(order)
    ? stations
    : toIdArray(order?.ready_printer_ids);
  const id = Number(stationId);
  ready = ready.includes(id) ? ready.filter((r) => r !== id) : [...ready, id];
  const allReady = stations.length > 0 && stations.every((s) => ready.includes(s));
  return {
    kitchen_status: allReady ? KITCHEN_STATUS_READY : 0,
    ready_printer_ids: ready,
  };
}

export function parseCreatedAt(order) {
  if (!order) return null;
  if (order.created_at) {
    const ms = moment(order.created_at).valueOf();
    if (!Number.isNaN(ms)) return ms;
  }
  if (order.created_at_time) {
    const composed = `${moment().format("YYYY-MM-DD")} ${order.created_at_time}`;
    const ms = moment(composed, "YYYY-MM-DD HH:mm").valueOf();
    if (!Number.isNaN(ms)) return ms;
  }
  return null;
}

// Format an elapsed milliseconds span as M:SS, switching to H:MM:SS past 1h.
export function formatElapsed(ms) {
  if (ms == null || ms < 0) return "0:00";
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}:${String(sec).padStart(2, "0")}`;
  const hr = Math.floor(min / 60);
  return `${hr}:${String(min % 60).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// KDS "Language" setting → the payload key holding that name column.
// The BE ships all four on every chit row (item, modifier and choice alike)
// and already falls back to Name 1 when the chosen column is blank.
export const NAME_FIELD_KEYS = {
  name1: "name",
  name2: "name_2",
  name3: "name_3",
  name4: "name_4",
};
